import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FEATURE_FLAG_KEYS } from '@/features/open-feature'
import { Button, Text } from '@/ui'
import { PostTaxYearFilingCard } from './PostTaxYearFilingCard'
import { TypographyStyles } from '@/styles'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useLoadedFlagValue } from '@/utils/sharedHooks'
import {
  fetchPostTaxYearStatus,
  PostTaxYearState,
  type PostTaxYearStatus,
} from '@/features/taxes/taxfyle-short-form/taxfyleShortForm.slice'

/**
 * Filing a tax year after it has turned over. The year-end home is on the new
 * season by then, so this is where a straggler picks their prior year back up.
 */
/**
 * Null while the year has nothing to file, and for anyone outside the rollout.
 */
export const usePostTaxYearStatus = (year: string) => {
  const dispatch = useAppDispatch()
  const [status, setStatus] = useState<PostTaxYearStatus | null>(null)
  // The archive renders a year per row, so without this every user outside the
  // rollout fires a request per row that the gated route answers with a 404.
  // Null until the provider resolves: useBooleanFlagValue would read false on a
  // cold load and settle there, leaving the row permanently empty.
  const enabled = useLoadedFlagValue(
    FEATURE_FLAG_KEYS.postTaxYearEnabled,
    false
  )

  const load = useCallback(async () => {
    const data = await fetchPostTaxYearStatus(year)(dispatch)
    setStatus(
      data && data.state !== PostTaxYearState.notApplicable ? data : null
    )
  }, [dispatch, year])

  useEffect(() => {
    if (enabled === null) {
      return
    }
    if (!enabled) {
      setStatus(null)
      return
    }
    load()
  }, [enabled, load])

  // The card can change the state it is rendering, so it needs a way to ask for
  // it again rather than waiting for a remount.
  return { status, refresh: load }
}

export const PostTaxYearFiling = ({
  year,
  status,
  onUpdated,
}: {
  year: string
  status: PostTaxYearStatus | null
  onUpdated?: () => void
}) => {
  const navigate = useNavigate()

  if (!status) {
    return null
  }

  if (
    status.state === PostTaxYearState.withTaxPreparer ||
    status.state === PostTaxYearState.awaitingHandoff
  ) {
    return (
      <div className="mb-8 flex w-full flex-col gap-4">
        {status.forms
          .filter((form) => form.answered)
          .map((form) => (
            <PostTaxYearFilingCard
              key={form.filingFormId}
              form={form}
              taxYear={year}
              isTwoFormFiler={status.forms.length > 1}
              onUpdated={onUpdated}
            />
          ))}
        {status.canEdit && (
          <div className="flex justify-center">
            <Button
              variant="secondary"
              onClick={() => navigate(`/taxes/annual/short_form?year=${year}`)}
            >
              Review your {year} responses
            </Button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="mb-8 flex w-full flex-col items-center gap-4 text-center">
      <Text variant={TypographyStyles.BODY_SM}>
        {status.booksLocked
          ? `Please complete the questionnaire below. We will connect you to a tax preparer once it is submitted.`
          : `Please complete the questionnaire below to the best of your knowledge. We will connect you to a tax preparer once your ${year} books are finalized.`}
      </Text>
      <Button onClick={() => navigate(`/taxes/annual/short_form?year=${year}`)}>
        Complete your {year} tax questionnaire
      </Button>
    </div>
  )
}
